import { create } from 'zustand';
import { CollectionInterface, Permissions } from "ts-interfaces";
import { useLoginState } from '../studioxr/auth/LoginState';
import { 
  getCollections, 
  getSharedCollections, 
  getOwnedProjectCollections, 
  getSharedProjectCollections 
} from '../studioxr/auth/LoginState';
import { curatedTemplateUserID } from '../libs/firebase';

interface CollectionDetailed extends CollectionInterface {
  isEditable: boolean;
  collectionType: 'individual' | 'shared' | 'ownedProject' | 'sharedProject';
}

interface CollectionDetailedState {
  // Individual collections (owned by user)
  myIndividualCollections: CollectionDetailed[];
  
  // Shared individual collections (from collectionRead/collectionWrite permissions)
  sharedIndividualCollections: CollectionDetailed[];
  
  // My project collections (from projects I own)
  myProjectCollections: CollectionDetailed[];
  
  // Shared project collections (from projectRead/projectWrite permissions)
  sharedProjectCollections: CollectionDetailed[];
  
  // All collections combined for convenience
  allCollections: CollectionDetailed[];
  
  // Editable collections only
  editableCollections: CollectionDetailed[];
  
  // Curated collections
  curatedCollections: CollectionInterface[];
  curatedCollectionsLoaded: boolean;
  
  // 🔧 OPTIMIZATION 2: More specific loading states to prevent unnecessary renders
  isLoading: boolean;
  isFetchingCurated: boolean;
  isFetchingUserCollections: boolean;
  initialFetchDone: boolean;
  
  // Current user context
  currentUserId: string | null;
  permissionsRef: Permissions | null;
  
  // Teams PRO access getters
  getTeamsProAccess: () => {
    isProUser: boolean;
    isTeamsProUser: boolean;
  };
  
  // Actions
  fetchCollections: (userId: string, permissions: Permissions | null) => Promise<void>;
  getMoreCollections: (type: 'individual' | 'shared' | 'ownedProject' | 'sharedProject' | 'all', limit?: number) => Promise<void>;
  refreshCollections: () => Promise<void>;
  checkEditability: (collection: CollectionInterface, userId: string) => boolean;
  
  // Curated collections methods
  fetchCuratedCollections: () => Promise<void>;
  getMoreCuratedCollections: () => Promise<void>;
  
  // Collection operations
  deleteCollection: (collection: CollectionInterface, currentUserUid: string) => Promise<void>;
  applyProFiltering: (collections: {
    myIndividualCollections: CollectionDetailed[];
    sharedIndividualCollections: CollectionDetailed[];
    myProjectCollections: CollectionDetailed[];
    sharedProjectCollections: CollectionDetailed[];
  }) => {
    myIndividualCollections: CollectionDetailed[];
    sharedIndividualCollections: CollectionDetailed[];
    myProjectCollections: CollectionDetailed[];
    sharedProjectCollections: CollectionDetailed[];
  };
  
  // Setters
  setCollections: (collections: CollectionInterface[]) => void;
  updateCollection: (updatedCollection: CollectionInterface) => void;
  removeCollection: (collectionId: string) => void;
}

export const useCollectionDetailed = create<CollectionDetailedState>((set, get) => {
  // 🔧 MEMOIZATION: Create stable function references to prevent re-renders
  const fetchCollections = async (userId: string, permissions: Permissions | null) => {
    if (!userId) return;
    
    // 🔧 OPTIMIZATION 2: Use specific loading state for user collections
    set({ 
      isFetchingUserCollections: true, 
      isLoading: true, 
      currentUserId: userId, 
      permissionsRef: permissions 
    });
    
    try {
             // Fetch all different types of collections in parallel
       const [
         myIndividualRaw,
         sharedIndividualRaw,
         myProjectRaw,
         sharedProjectRaw
       ] = await Promise.all([
         getCollections(userId, [], 20), // Individual collections owned by user
         getSharedCollections(userId, [], 20), // Shared individual collections
         getOwnedProjectCollections(userId, [], 20), // Project collections from owned projects
         getSharedProjectCollections(userId, [], 20) // Project collections from shared projects
       ]) as [CollectionInterface[], CollectionInterface[], CollectionInterface[], CollectionInterface[]];

      // Add metadata to collections
      const myIndividualCollections: CollectionDetailed[] = myIndividualRaw.map(collection => ({
        ...collection,
        isEditable: get().checkEditability(collection, userId),
        collectionType: 'individual' as const
      }));

      const sharedIndividualCollections: CollectionDetailed[] = sharedIndividualRaw.map(collection => ({
        ...collection,
        isEditable: get().checkEditability(collection, userId),
        collectionType: 'shared' as const
      }));

      const myProjectCollections: CollectionDetailed[] = myProjectRaw.map(collection => ({
        ...collection,
        isEditable: get().checkEditability(collection, userId),
        collectionType: 'ownedProject' as const
      }));

      const sharedProjectCollections: CollectionDetailed[] = sharedProjectRaw.map(collection => ({
        ...collection,
        isEditable: get().checkEditability(collection, userId),
        collectionType: 'sharedProject' as const
      }));

      // Apply PRO-based filtering
      const filteredCollections = get().applyProFiltering({
        myIndividualCollections,
        sharedIndividualCollections,
        myProjectCollections,
        sharedProjectCollections
      });

      // Combine all filtered collections
      const allCollections = [
        ...filteredCollections.myIndividualCollections,
        ...filteredCollections.sharedIndividualCollections,
        ...filteredCollections.myProjectCollections,
        ...filteredCollections.sharedProjectCollections
      ];

      // Filter editable collections
      const editableCollections = allCollections.filter(collection => collection.isEditable);

      // 🔧 OPTIMIZATION 2: Batch state updates to prevent multiple renders
      set({
        myIndividualCollections: filteredCollections.myIndividualCollections,
        sharedIndividualCollections: filteredCollections.sharedIndividualCollections,
        myProjectCollections: filteredCollections.myProjectCollections,
        sharedProjectCollections: filteredCollections.sharedProjectCollections,
        allCollections,
        editableCollections,
        isLoading: false,
        isFetchingUserCollections: false,
        initialFetchDone: true
      });

    } catch (error) {
      console.error('Error fetching detailed collections:', error);
      // 🔧 OPTIMIZATION 2: Reset both loading states on error
      set({ 
        isLoading: false, 
        isFetchingUserCollections: false 
      });
    }
  };

  return {
    myIndividualCollections: [],
    sharedIndividualCollections: [],
    myProjectCollections: [],
    sharedProjectCollections: [],
    allCollections: [],
    editableCollections: [],
    curatedCollections: [],
    curatedCollectionsLoaded: false,
    isLoading: false,
    isFetchingCurated: false,
    isFetchingUserCollections: false,
    initialFetchDone: false,
    currentUserId: null,
    permissionsRef: null,

    getTeamsProAccess: () => {
      const loginState = useLoginState.getState();
      return {
        isProUser: loginState.isProUser,
        isTeamsProUser: loginState.isTeamsProUser,
      };
    },

  checkEditability: (collection: CollectionInterface, userId: string): boolean => {
    if (!userId || !collection) return false;
    
    // User owns the collection
    if (collection.uid === userId) return true;
    
    // Check if user has write access via writeAccess array
    if (collection.writeAccess && Array.isArray(collection.writeAccess)) {
      return collection.writeAccess.includes(userId);
    }
    
    return false;
  },

  applyProFiltering: (collections) => {
    const { isProUser, isTeamsProUser } = get().getTeamsProAccess();
    const currentUserId = get().currentUserId;
    
    // Helper function to remove duplicates from myIndividualCollections
    const removeDuplicatesFromIndividual = (
      individual: CollectionDetailed[], 
      ...otherArrays: CollectionDetailed[][]
    ): CollectionDetailed[] => {
      const otherCollectionIds = new Set(
        otherArrays.flat().map(collection => collection.id)
      );
      return individual.filter(collection => !otherCollectionIds.has(collection.id));
    };
    
    // Teams PRO users see all collections but remove duplicates from individual
    if (isTeamsProUser) {
      const deduplicatedIndividual = removeDuplicatesFromIndividual(
        collections.myIndividualCollections,
        collections.myProjectCollections,
        collections.sharedProjectCollections
      );
      
      return {
        myIndividualCollections: deduplicatedIndividual,
        sharedIndividualCollections: collections.sharedIndividualCollections,
        myProjectCollections: collections.myProjectCollections,
        sharedProjectCollections: collections.sharedProjectCollections
      };
    }
    
    // PRO users see their individual collections + their owned project collections
    // Remove duplicates from individual that exist in project collections
    if (isProUser) {
      const ownedProjectCollections = collections.myProjectCollections.filter(c => c.uid === currentUserId);
      const deduplicatedIndividual = removeDuplicatesFromIndividual(
        collections.myIndividualCollections,
        ownedProjectCollections
      );


      return {
        myIndividualCollections: deduplicatedIndividual,
        sharedIndividualCollections: [], // No shared individual collections for PRO users
        myProjectCollections: ownedProjectCollections,
        sharedProjectCollections: [] // No shared project collections for PRO users
      };
    }
    
    // Non-PRO users see collections as-is (no deduplication)
    return {
      myIndividualCollections: collections.myIndividualCollections,
      sharedIndividualCollections: [],
      myProjectCollections: [],
      sharedProjectCollections: []
    };
  },

    // 🔧 MEMOIZATION: Use the stable function reference
    fetchCollections,

  getMoreCollections: async (type: 'individual' | 'shared' | 'ownedProject' | 'sharedProject' | 'all', limit = 20) => {
    const state = get();
    const { currentUserId } = state;
    
    if (!currentUserId) return;
    
    // 🔧 OPTIMIZATION 2: Use specific loading state for incremental fetches
    set({ isFetchingUserCollections: true });
    
    try {
      let newCollections: CollectionInterface[] = [];
      
             switch (type) {
         case 'individual':
           newCollections = await getCollections(currentUserId, state.myIndividualCollections, limit) as CollectionInterface[];
           break;
         case 'shared':
           newCollections = await getSharedCollections(currentUserId, state.sharedIndividualCollections, limit) as CollectionInterface[];
           break;
         case 'ownedProject':
           newCollections = await getOwnedProjectCollections(currentUserId, state.myProjectCollections, limit) as CollectionInterface[];
           break;
         case 'sharedProject':
           newCollections = await getSharedProjectCollections(currentUserId, state.sharedProjectCollections, limit) as CollectionInterface[];
           break;
        case 'all':
                     // Fetch more of all types
           const [moreIndividual, moreShared, moreOwnedProject, moreSharedProject] = await Promise.all([
             getCollections(currentUserId, state.myIndividualCollections, Math.ceil(limit/4)),
             getSharedCollections(currentUserId, state.sharedIndividualCollections, Math.ceil(limit/4)),
             getOwnedProjectCollections(currentUserId, state.myProjectCollections, Math.ceil(limit/4)),
             getSharedProjectCollections(currentUserId, state.sharedProjectCollections, Math.ceil(limit/4))
           ]) as [CollectionInterface[], CollectionInterface[], CollectionInterface[], CollectionInterface[]];
          
          // Process each type separately
          const processedIndividual = moreIndividual.map(collection => ({
            ...collection,
            isEditable: get().checkEditability(collection, currentUserId),
            collectionType: 'individual' as const
          }));
          
          const processedShared = moreShared.map(collection => ({
            ...collection,
            isEditable: get().checkEditability(collection, currentUserId),
            collectionType: 'shared' as const
          }));
          
          const processedOwnedProject = moreOwnedProject.map(collection => ({
            ...collection,
            isEditable: get().checkEditability(collection, currentUserId),
            collectionType: 'ownedProject' as const
          }));
          
          const processedSharedProject = moreSharedProject.map(collection => ({
            ...collection,
            isEditable: get().checkEditability(collection, currentUserId),
            collectionType: 'sharedProject' as const
          }));
          
          // Update state with new collections
          set(state => {
            const newMyIndividual = [...state.myIndividualCollections, ...processedIndividual];
            const newSharedIndividual = [...state.sharedIndividualCollections, ...processedShared];
            const newMyProject = [...state.myProjectCollections, ...processedOwnedProject];
            const newSharedProject = [...state.sharedProjectCollections, ...processedSharedProject];
            
            // Apply PRO-based filtering
            const filteredCollections = get().applyProFiltering({
              myIndividualCollections: newMyIndividual,
              sharedIndividualCollections: newSharedIndividual,
              myProjectCollections: newMyProject,
              sharedProjectCollections: newSharedProject
            });
            
            const newAllCollections = [
              ...filteredCollections.myIndividualCollections,
              ...filteredCollections.sharedIndividualCollections,
              ...filteredCollections.myProjectCollections,
              ...filteredCollections.sharedProjectCollections
            ];
            const newEditableCollections = newAllCollections.filter(collection => collection.isEditable);
            
            return {
              myIndividualCollections: filteredCollections.myIndividualCollections,
              sharedIndividualCollections: filteredCollections.sharedIndividualCollections,
              myProjectCollections: filteredCollections.myProjectCollections,
              sharedProjectCollections: filteredCollections.sharedProjectCollections,
              allCollections: newAllCollections,
              editableCollections: newEditableCollections,
              isFetchingUserCollections: false
            };
          });
          return;
      }
      
      // Process new collections for single type
      const processedCollections: CollectionDetailed[] = newCollections.map(collection => ({
        ...collection,
        isEditable: get().checkEditability(collection, currentUserId),
        collectionType: type as any
      }));
      
      // Update state based on type
      set(state => {
        let updatedState: any = { isFetchingUserCollections: false };
        
        switch (type) {
          case 'individual':
            updatedState.myIndividualCollections = [...state.myIndividualCollections, ...processedCollections];
            break;
          case 'shared':
            updatedState.sharedIndividualCollections = [...state.sharedIndividualCollections, ...processedCollections];
            break;
          case 'ownedProject':
            updatedState.myProjectCollections = [...state.myProjectCollections, ...processedCollections];
            break;
          case 'sharedProject':
            updatedState.sharedProjectCollections = [...state.sharedProjectCollections, ...processedCollections];
            break;
        }
        
        // Apply PRO-based filtering to the updated collections
        const collectionsToFilter = {
          myIndividualCollections: updatedState.myIndividualCollections || state.myIndividualCollections,
          sharedIndividualCollections: updatedState.sharedIndividualCollections || state.sharedIndividualCollections,
          myProjectCollections: updatedState.myProjectCollections || state.myProjectCollections,
          sharedProjectCollections: updatedState.sharedProjectCollections || state.sharedProjectCollections
        };
        
        const filteredCollections = get().applyProFiltering(collectionsToFilter);
        
        // Update the state with filtered collections
        updatedState.myIndividualCollections = filteredCollections.myIndividualCollections;
        updatedState.sharedIndividualCollections = filteredCollections.sharedIndividualCollections;
        updatedState.myProjectCollections = filteredCollections.myProjectCollections;
        updatedState.sharedProjectCollections = filteredCollections.sharedProjectCollections;
        
        // Recalculate combined arrays
        const newAllCollections = [
          ...filteredCollections.myIndividualCollections,
          ...filteredCollections.sharedIndividualCollections,
          ...filteredCollections.myProjectCollections,
          ...filteredCollections.sharedProjectCollections
        ];
        
        updatedState.allCollections = newAllCollections;
        updatedState.editableCollections = newAllCollections.filter(collection => collection.isEditable);
        
        return updatedState;
      });
      
    } catch (error) {
      console.error(`Error fetching more ${type} collections:`, error);
      set({ isFetchingUserCollections: false });
    }
  },

  refreshCollections: async () => {
    const state = get();
    if (!state.currentUserId) return;
    
    // Reset state and fetch fresh data
    set({
      myIndividualCollections: [],
      sharedIndividualCollections: [],
      myProjectCollections: [],
      sharedProjectCollections: [],
      allCollections: [],
      editableCollections: [],
      initialFetchDone: false
    });
    
    await get().fetchCollections(state.currentUserId, state.permissionsRef);
  },

  fetchCuratedCollections: async () => {
    // 🔧 OPTIMIZATION 2: Don't set general isLoading for curated collections
    try {
      set({ isFetchingCurated: true });
      const curatedCollections = await getCollections(curatedTemplateUserID, [], 20) as CollectionInterface[];
      set({ 
        curatedCollections,
        curatedCollectionsLoaded: curatedCollections.length < 20,
        isFetchingCurated: false 
      });
    } catch (error) {
      console.error('Error fetching curated collections:', error);
      set({ isFetchingCurated: false });
    }
  },

  getMoreCuratedCollections: async () => {
    const state = get();
    if (state.curatedCollectionsLoaded) return;
    
    try {
      set({ isFetchingCurated: true });
      const newCuratedCollections = await getCollections(curatedTemplateUserID, state.curatedCollections, 20) as CollectionInterface[];
      const mergedCollections = [...state.curatedCollections, ...newCuratedCollections];
      
      set({
        curatedCollections: mergedCollections,
        curatedCollectionsLoaded: newCuratedCollections.length < 20,
        isFetchingCurated: false
      });
    } catch (error) {
      console.error('Error fetching more curated collections:', error);
      set({ isFetchingCurated: false });
    }
  },

  deleteCollection: async (collection: CollectionInterface, currentUserUid: string) => {
    if (!currentUserUid) throw new Error("User not logged in");
    
    try {
      // Use the existing delete method from LoginState
      await useLoginState.getState().deleteCollection(collection, currentUserUid);
      
      // Remove from local state
      get().removeCollection(collection.id);
    } catch (error) {
      console.error('Error deleting collection:', error);
      throw error; // Re-throw to let the component handle the error display
    }
  },

  setCollections: (collections: CollectionInterface[]) => {
    const state = get();
    if (!state.currentUserId) return;
    
    // Process collections and categorize them
    const processedCollections: CollectionDetailed[] = collections.map(collection => ({
      ...collection,
      isEditable: get().checkEditability(collection, state.currentUserId!),
      collectionType: determineCollectionType(collection, state.currentUserId!)
    }));
    
    // Categorize collections
    const myIndividualCollections = processedCollections.filter(c => c.collectionType === 'individual');
    const sharedIndividualCollections = processedCollections.filter(c => c.collectionType === 'shared');
    const myProjectCollections = processedCollections.filter(c => c.collectionType === 'ownedProject');
    const sharedProjectCollections = processedCollections.filter(c => c.collectionType === 'sharedProject');
    
    // Apply PRO-based filtering
    const filteredCollections = get().applyProFiltering({
      myIndividualCollections,
      sharedIndividualCollections,
      myProjectCollections,
      sharedProjectCollections
    });
    
    // Combine all filtered collections
    const allCollections = [
      ...filteredCollections.myIndividualCollections,
      ...filteredCollections.sharedIndividualCollections,
      ...filteredCollections.myProjectCollections,
      ...filteredCollections.sharedProjectCollections
    ];
    
    const editableCollections = allCollections.filter(collection => collection.isEditable);
    
    set({
      myIndividualCollections: filteredCollections.myIndividualCollections,
      sharedIndividualCollections: filteredCollections.sharedIndividualCollections,
      myProjectCollections: filteredCollections.myProjectCollections,
      sharedProjectCollections: filteredCollections.sharedProjectCollections,
      allCollections: allCollections,
      editableCollections
    });
  },

  updateCollection: (updatedCollection: CollectionInterface) => {
    const state = get();
    if (!state.currentUserId) return;
    
    const processedCollection: CollectionDetailed = {
      ...updatedCollection,
      isEditable: get().checkEditability(updatedCollection, state.currentUserId),
      collectionType: determineCollectionType(updatedCollection, state.currentUserId)
    };
    
    set(state => {
      const updateInArray = (array: CollectionDetailed[]) => {
        const index = array.findIndex(c => c.id === updatedCollection.id);
        if (index >= 0) {
          const newArray = [...array];
          newArray[index] = processedCollection;
          return newArray;
        }
        return array;
      };
      
      const newMyIndividual = updateInArray(state.myIndividualCollections);
      const newSharedIndividual = updateInArray(state.sharedIndividualCollections);
      const newMyProject = updateInArray(state.myProjectCollections);
      const newSharedProject = updateInArray(state.sharedProjectCollections);
      
      // Apply PRO-based filtering
      const filteredCollections = get().applyProFiltering({
        myIndividualCollections: newMyIndividual,
        sharedIndividualCollections: newSharedIndividual,
        myProjectCollections: newMyProject,
        sharedProjectCollections: newSharedProject
      });
      
      const newAllCollections = [
        ...filteredCollections.myIndividualCollections,
        ...filteredCollections.sharedIndividualCollections,
        ...filteredCollections.myProjectCollections,
        ...filteredCollections.sharedProjectCollections
      ];
      const newEditableCollections = newAllCollections.filter(collection => collection.isEditable);
      
      return {
        myIndividualCollections: filteredCollections.myIndividualCollections,
        sharedIndividualCollections: filteredCollections.sharedIndividualCollections,
        myProjectCollections: filteredCollections.myProjectCollections,
        sharedProjectCollections: filteredCollections.sharedProjectCollections,
        allCollections: newAllCollections,
        editableCollections: newEditableCollections
      };
    });
  },

  removeCollection: (collectionId: string) => {
    set(state => {
      const filterOut = (array: CollectionDetailed[]) => array.filter(c => c.id !== collectionId);
      
      const newMyIndividual = filterOut(state.myIndividualCollections);
      const newSharedIndividual = filterOut(state.sharedIndividualCollections);
      const newMyProject = filterOut(state.myProjectCollections);
      const newSharedProject = filterOut(state.sharedProjectCollections);
      
      // Apply PRO-based filtering
      const filteredCollections = get().applyProFiltering({
        myIndividualCollections: newMyIndividual,
        sharedIndividualCollections: newSharedIndividual,
        myProjectCollections: newMyProject,
        sharedProjectCollections: newSharedProject
      });
      
      const newAllCollections = [
        ...filteredCollections.myIndividualCollections,
        ...filteredCollections.sharedIndividualCollections,
        ...filteredCollections.myProjectCollections,
        ...filteredCollections.sharedProjectCollections
      ];
      const newEditableCollections = newAllCollections.filter(collection => collection.isEditable);
      
      return {
        myIndividualCollections: filteredCollections.myIndividualCollections,
        sharedIndividualCollections: filteredCollections.sharedIndividualCollections,
        myProjectCollections: filteredCollections.myProjectCollections,
        sharedProjectCollections: filteredCollections.sharedProjectCollections,
        allCollections: newAllCollections,
        editableCollections: newEditableCollections
      };
    });
  },
}});

// Helper function to determine collection type
function determineCollectionType(collection: CollectionInterface, currentUserId: string): 'individual' | 'shared' | 'ownedProject' | 'sharedProject' {
  // If it has an associated project, it's a project collection
  if (collection.associatedProjectId) {
    // Check if user owns the collection
    if (collection.uid === currentUserId) {
      return 'ownedProject';
    } else {
      return 'sharedProject';
    }
  }
  
  // If no associated project, it's an individual collection
  if (collection.uid === currentUserId) {
    return 'individual';
  } else {
    return 'shared';
  }
} 